import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css, cx } from '@linaria/core'
import { useTranslation } from 'react-i18next'
import { sanitizeProps, useEvent, useRandomId, useResizeObserver } from '@fs/zion-frontend-friends'
import { Expander, ExpanderContent } from '../expander'
import { InlineButton } from '../buttons'

const contentsCss = css`
  /* Remove bottom padding from last paragraph in children so the more/less button will hug a little tighter. */
  & p:last-of-type {
    padding-bottom: 0;
  }
`

const collapsedCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--line-clamp);
`

const buttonContainerCss = css`
  margin-top: 4px;
`

function getPx(value) {
  const numStr = value.match(/\d+/)
  return numStr ? parseInt(numStr, 10) : 0 // this 0 is a failsafe for JSDOM testing. Manual testing in browsers proved that numStr is always defined.
}

function getTextContainer(el) {
  let textChild = el
  while (textChild.firstChild && textChild.firstChild.nodeType !== Node.TEXT_NODE) {
    textChild = textChild.firstElementChild
  }
  return textChild
}

export default function MoreLess({ children, collapsedLineCount = 2, buttonProps, ...props }) {
  const [open, setOpen] = useState(false)
  const [collapsedHeight, setCollapsedHeight] = useState(0)
  const [clampLines, setClampLines] = useState(true)
  const [expandable, setExpandable] = useState(false)
  const [t] = useTranslation()
  const buttonRef = useRef(null)
  const textRef = useRef(null)
  const expandableTextRef = useRef(null)

  // needed for resizeObserver to update (otherwise, doesn't recognize if a ref changes to a different DOM node)
  const activeRef = useMemo(() => (expandable ? expandableTextRef : textRef), [expandable])

  const onTransitionEnd = useCallback(() => setClampLines(true), [])
  const handleOpen = useEvent((nextStateOrFunctor) => {
    setOpen(nextStateOrFunctor)
    // When expanding, disable clamping lines
    setClampLines(false)

    // Reset scroll position to the top when expanding, this is needed when
    // the user has tabbed to an element offscreen (which opens the expander)
    // the scroll position should be reset to the top, because otherwise when
    // the user clicks the "Less" button to close the expander, it will be
    // positioned to the bottom of the text, which is not the expected behavior.
    if (expandableTextRef.current) expandableTextRef.current.scrollTop = 0
  })
  const handleClick = useEvent(() => handleOpen((o) => !o))

  // Calculate the collapsed height and check if the expander should be used
  const calcExpandable = useCallback(
    (node) => {
      // When clamped, the clientHeight is only what's visible. Scroll height in that case is the right option.
      const clientHeight = Math.max(node.clientHeight, node.scrollHeight)
      // if there is no clientHeight (e.g. when hidden in another expander), we cannot calculate whether or not MoreLess is expandable, so any calculations we do below are useless.
      if (!clientHeight) return

      const textChild = getTextContainer(node)
      const compStyle = getComputedStyle(textChild)
      const compLineHeight = compStyle.getPropertyValue('line-height')
      let lineHeight = ''

      if (compLineHeight === 'normal') {
        const fontSize = getPx(compStyle.getPropertyValue('font-size'))
        lineHeight = fontSize * 1.375 // this is the value of line-height of noto-sans when it is 'normal', rather than a number
      } else {
        lineHeight = getPx(compLineHeight)
      }

      const height = lineHeight * collapsedLineCount
      const paddingTop = getPx(compStyle.getPropertyValue('padding-top'))
      const paddingBottom = getPx(compStyle.getPropertyValue('padding-bottom'))
      const totalCollapsedHeight = height + paddingTop // No padding bottom because it exposes extra lines when the line-height is close together

      // In firefox the (totalCollapsedHeight + padding) is < the expandedChildHeight, but only by a fraction of a px.
      const canCollapse = clientHeight - (totalCollapsedHeight + paddingBottom) > 1

      setCollapsedHeight(totalCollapsedHeight)
      setExpandable(canCollapse)
    },
    [collapsedLineCount]
  )

  // recalculate expandablility when childrens change and when it resizes
  useLayoutEffect(() => calcExpandable(activeRef.current), [children, calcExpandable, activeRef])
  useResizeObserver(activeRef, () => calcExpandable(activeRef.current))

  const controlsId = useRandomId()

  return (
    <div {...sanitizeProps(props)}>
      {/* We need to not render Expander on first render so MoreLess can calculate the collapsedHeight as quickly as possible. Expander complicates these calculations because it controls height and display logic using the collapsedHeight. */}
      {!expandable ? (
        <div
          ref={textRef}
          className={cx(clampLines && collapsedCss, contentsCss)}
          style={{ '--line-clamp': collapsedLineCount }}
        >
          {children}
        </div>
      ) : (
        <>
          <Expander open={open} setOpen={handleOpen} ariaControlsId={controlsId}>
            <ExpanderContent
              collapsedHeight={collapsedHeight}
              // Only give it the transition end handler when it is going to be collapsed (i.e. they just clicked the "Less" button)
              onTransitionEnd={!open ? onTransitionEnd : null}
            >
              <div
                ref={expandableTextRef}
                className={cx(clampLines && collapsedCss, contentsCss)}
                style={{ '--line-clamp': collapsedLineCount }}
              >
                {children}
              </div>
            </ExpanderContent>
          </Expander>
          <div className={buttonContainerCss}>
            <InlineButton
              {...sanitizeProps(buttonProps)}
              onClick={handleClick}
              aria-expanded={open}
              ref={buttonRef}
              aria-controls={controlsId}
            >
              {open ? t('common-ui:show-less.info.abbr', 'Less') : t('common-ui:show-more.info.abbr', 'More')}
            </InlineButton>
          </div>
        </>
      )}
    </div>
  )
}

MoreLess.propTypes = {
  /** The text component to truncate. */
  children: PropTypes.node.isRequired,

  /** The number of lines to show when the text is collapsed. */
  collapsedLineCount: PropTypes.number,

  /** Add custom props to the more/less button. */
  buttonProps: PropTypes.object,
}
